import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import clsx from "clsx";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../hook/useRegistrationStepFocus";
import AuthenticationApiSerivce from "../../api/AuthServices";
import { resolveUserSubmitErrorMessage } from "../../api/submitRequestErrors";
import PaymentSetupWrapper from "./PaymentSetup.style";
import { RegistrationLayout } from "../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../RegistrationLayout/RegistrationSetupNavbar";
import {
  API_SERVICE_ID,
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../helper/constants";
import { extractPublishableKey } from "../RegistrationSetup/CardOnFile/RegistrationCardOnFile/RegistrationCardOnFile.validator";
import {
  getPostSavePaymentTypeRoute,
  getStoredPaymentTypeFields,
  parseSavePaymentTypeResponse,
} from "../RegistrationSetup/registrationPaymentType";
import { registrationStepBack } from "../RegistrationSetup/registrationNavigation";
import {
  redirectIfProviderUnavailableBeforeApi,
  redirectIfProviderUnavailableFromResponse,
} from "../RegistrationSetup/registrationRdAvailability";
import {
  trackRegistrationError,
  trackRegistrationPaymentTypeSelected,
  trackRegistrationStepCompleted,
} from "../../helper/registrationAnalytics";
import { REGISTRATION_LINK } from "../RegistrationLayout/registrationTheme";
import { RegistrationSelectedCheck, RegistrationContinueArrow } from "../RegistrationLayout/RegistrationFieldError";

const PAYMENT_TYPE_STEP = REGISTRATION_SETUP_STEP_META.PAYMENT_TYPE;

const PAYMENT_OPTIONS = [
  {
    id: "insurance",
    titleKey: "NUTRITION_PAYMENT_LBL_USE_INSURANCE",
    descKey: "NUTRITION_PAYMENT_DESC_USE_INSURANCE",
  },
  {
    id: "selfpay",
    titleKey: "NUTRITION_PAYMENT_LBL_SELF_PAY",
    descKey: "NUTRITION_PAYMENT_DESC_SELF_PAY",
  },
];

const CheckIcon = () => <RegistrationSelectedCheck />;

const PaymentSetup = ({ history }) => {
  const { t, i18n } = useTranslation();
  const [paymentType, setPaymentType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const stepTitleRef = useRegistrationStepFocus();

  const renderPaymentIcon = (id) =>
    id === "insurance" ? (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={REGISTRATION_LINK}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ) : (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={REGISTRATION_LINK}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const saved = localStorage.getItem("data");
    if (!saved) {
      history.replace("/register/account");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    try {
      const parsed = JSON.parse(saved);
      if (!parsed.firstName || !parsed.email || !parsed.categoryId) {
        history.replace("/register/account");
      } else {
        const { paymentType: savedPaymentType } =
          getStoredPaymentTypeFields(parsed);
        if (savedPaymentType === "selfpay") {
          setPaymentType("selfpay");
        } else if (savedPaymentType === "insurance") {
          setPaymentType("insurance");
        }
      }
    } catch {
      history.replace("/register/account");
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) root.style.height = prevRootHeight;
    };
  }, [history]);

  const handleContinue = async () => {
    if (!paymentType || isSubmittingRef.current) return;

    const saved = localStorage.getItem("data");
    if (!saved) {
      history.replace("/register/account");
      return;
    }
    let accountData = {};
    try {
      accountData = JSON.parse(saved);
    } catch {
      history.replace("/register/account");
      return;
    }

    if (redirectIfProviderUnavailableBeforeApi(history, accountData)) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const saveRes = await AuthenticationApiSerivce.savePaymentType(
        {
          service_id: API_SERVICE_ID,
          payment_type: paymentType,
        },
        i18n.language
      );

      if (redirectIfProviderUnavailableFromResponse(history, saveRes)) {
        return;
      }

      if (!saveRes?.status) {
        trackRegistrationError({
          stepName: "payment",
          stepNumber: 2,
          errorType: "api_failed",
        });
        message.error(
          saveRes?.message || t("NUTRITION_PAYMENT_ERR_SAVE_TYPE")
        );
        return;
      }

      const { paymentType: savedPaymentType, isCardPresent } =
        parseSavePaymentTypeResponse(saveRes);

      accountData.paymentType = savedPaymentType || paymentType;
      accountData.isCardPresent = isCardPresent;
      accountData.savePaymentTypeResponse = saveRes?.data ?? {};
      const stripePublishableKey = extractPublishableKey(saveRes);
      if (stripePublishableKey) {
        accountData.stripePublishableKey = stripePublishableKey;
      }
      localStorage.setItem("data", JSON.stringify(accountData));

      const nextRoute = getPostSavePaymentTypeRoute({
        paymentType: accountData.paymentType,
      });
      if (nextRoute) {
        trackRegistrationStepCompleted("payment", 2);
        history.push(nextRoute);
      }
    } catch (err) {
      trackRegistrationError({
        stepName: "payment",
        stepNumber: 2,
        errorType: "api_failed",
      });
      message.error(
        resolveUserSubmitErrorMessage(err, {
          fallback: t("NUTRITION_COMMON_ERR_TRY_AGAIN"),
          translate: t,
        })
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <PaymentSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={PAYMENT_TYPE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <div className="content-card-scroll">
              <div className="section-eyebrow">
                {t("NUTRITION_PAYMENT_LBL_TITLE_SETUP")}
              </div>
              <h2
                className="section-title"
                ref={stepTitleRef}
                {...REGISTRATION_STEP_TITLE_PROPS}
              >
                {t("NUTRITION_PAYMENT_LBL_TITLE")}
              </h2>
              <p className="section-sub">
                {t("NUTRITION_PAYMENT_DESC_SUBTITLE")}
              </p>

              <div
                className="pay-cards"
                role="group"
                aria-label={t("NUTRITION_PAYMENT_LBL_TITLE")}
              >
                {PAYMENT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={clsx(
                      "pay-card",
                      paymentType === option.id && "selected"
                    )}
                    aria-pressed={paymentType === option.id}
                    onClick={() => {
                      setPaymentType(option.id);
                      trackRegistrationPaymentTypeSelected(option.id);
                    }}
                  >
                    <div className="pay-card-icon">{renderPaymentIcon(option.id)}</div>
                    <div className="pay-card-name">{t(option.titleKey)}</div>
                    <div className="pay-card-desc">{t(option.descKey)}</div>
                    <div className="pay-card-check">
                      {paymentType === option.id && <CheckIcon />}
                    </div>
                  </button>
                ))}
              </div>

              <div className="notice-box">
                <div className="notice-icon" aria-hidden>ℹ️</div>
                <div className="notice-text">
                  {t("NUTRITION_PAYMENT_DESC_NOTICE")}
                </div>
              </div>

              <button
                type="button"
                className="continue-btn"
                onClick={handleContinue}
                disabled={!paymentType}
                aria-busy={isSubmitting || undefined}
              >
                {isSubmitting
                  ? `${t("MORE_CHANGE_LANGUAGE_BTN_CONTINUE")}…`
                  : (
                    <>
                      {t("MORE_CHANGE_LANGUAGE_BTN_CONTINUE")}
                      <RegistrationContinueArrow />
                    </>
                  )}
              </button>
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </PaymentSetupWrapper>
  );
};

export default compose(withRouter)(PaymentSetup);
